import { Analytics } from '@payu/paymentsos-types'

import apiConfig from '../../../api/AnalyticsApiHelpers/apiConfig'
import { stagesNames, PaymentStageNamesTypes } from '../components/paymentStagesConfig'

// view is added by the api request function
type PartialAnalyticsRequest = Omit<Analytics.AnalyticsRequest, 'view'>

const createFilters = (
  countries: string[],
  apps: string[],
  paymentStage?: PaymentStageNamesTypes
): Analytics.Filters => {
  const filters = []
  if (countries) {
    if (countries instanceof Array && countries.length !== 0) { // do nothing
      filters.push(createFilter(apiConfig.dimensions.country, countries))
    }
  }
  if (apps) {
    if (apps instanceof Array && apps.length !== 0) { // do nothing
      filters.push(createFilter(apiConfig.dimensions.application, apps))
    }
  }
  if (paymentStage) {
    filters.push(createFilter(apiConfig.dimensions.paymentStage, paymentStage))
  }
  return filters
}

const createFilter = (
  name: Analytics.Dimension,
  value: string | string[]
): Analytics.DimensionFilter => ({
  dimension: name,
  values: typeof value === 'string' ? [value] : value
})

export const forFilter = (
  from: Date,
  to: Date,
  dimension: Analytics.Dimension
): PartialAnalyticsRequest => ({
  time_range: {
    from: from as unknown as string,
    to: to as unknown as string,
    granularity: apiConfig.granularity.all
  },
  metrics: [{ name: apiConfig.metrics.totalAmount }],
  dimensions: [dimension]
})

export const forPaymentsFlowButtons = (
  from: Date,
  to: Date,
  countries: string[],
  apps: string[],
  metric: Analytics.Metric['name']
): PartialAnalyticsRequest => {
  const request: PartialAnalyticsRequest = {
    time_range: {
      from: (from as unknown as string),
      to: (to as unknown as string),
      granularity: apiConfig.granularity.all
    },
    dimensions: [apiConfig.dimensions.paymentStage],
    metrics: [{ name: metric }]
  }
  const filters = createFilters(countries, apps)
  if (filters.length !== 0) {
    request.filters = filters
  }
  return request
}

export interface BreakdownRequestsData {
  dimension: Analytics.Dimension,
  request: PartialAnalyticsRequest
}

export const forBreakdown = (
  from: Date,
  to: Date,
  countries: string[],
  apps: string[],
  paymentStage: PaymentStageNamesTypes,
  metric: Analytics.Metric['name']
): BreakdownRequestsData[] => {
  const dimensions = getDimensionsPerStage(paymentStage)
  const requestsData = []
  for (const dimension of dimensions) {
    const requestData : BreakdownRequestsData = {
      dimension,
      request: {
        time_range: {
          from: from as unknown as string,
          to: to as unknown as string,
          granularity: apiConfig.granularity.all
        },
        dimensions: [dimension],
        metrics: [{ name: metric }]
      }
    }
    const filters = createFilters(countries, apps, paymentStage)
    if (filters.length !== 0) {
      requestData.request.filters = filters
    }
    requestsData.push(requestData)
  }
  return requestsData
}

export const getDimensionsPerStage = (paymentStage: PaymentStageNamesTypes): Analytics.Dimensions => {
  switch (paymentStage) {
    case stagesNames.INITIATED:
      return [apiConfig.dimensions.currency]
    case stagesNames.BLOCKED:
      return [
        apiConfig.dimensions.currency,
        apiConfig.dimensions.cardVendor
      ]
    case stagesNames.VOIDED:
    case stagesNames.DECLINED:
    case stagesNames.SUCCESSFUL:
    case stagesNames.REFUND:
      return [
        apiConfig.dimensions.provider,
        apiConfig.dimensions.currency,
        apiConfig.dimensions.cardVendor
      ]
    default:
      return []
  }
}

export const forGraph = (
  from: Date,
  to: Date,
  granularity: Analytics.TimeRangeGranularity['granularity'],
  countries: string[],
  apps: string[],
  paymentStage: PaymentStageNamesTypes,
  metrics: Analytics.Metric['name'],
  dimension: Analytics.Dimension
): PartialAnalyticsRequest => {
  const request: PartialAnalyticsRequest = {
    time_range: {
      granularity,
      from: from as unknown as string,
      to: to as unknown as string
    },
    dimensions: [dimension],
    metrics: [{ name: metrics }],
    settings: { show_empty_buckets: true }
  }
  const filters = createFilters(countries, apps, paymentStage)
  if (filters.length !== 0) {
    request.filters = filters
  }
  return request
}
